/* eslint-disable max-lines */
import {
  GeneralAssemblyStatus,
  GeneralAssemblyType,
  VoteMethod,
  VoteChoice,
  ResolutionType,
  ResolutionMajorityArticle,
  CoOwnerEventType,
  VotePresency,
  ResolutionVoteResult,
  NotificationStatusReason,
  NotificationStatus,
} from 'backend/types';
import { RowType } from 'pages/Syndic/GeneralAssembly/Imports/CoOwners/utils';

const generalAssemblyTypes: Record<GeneralAssemblyType, string> = {
  [GeneralAssemblyType.COOWNERSHIP]: 'Copropriété',
  [GeneralAssemblyType.ASSOCIATION]: 'Association',
  [GeneralAssemblyType.COMPANY]: 'Entreprise',
};

const generalAssemblyStatus: Record<GeneralAssemblyStatus, string> = {
  [GeneralAssemblyStatus.DRAFT]: 'En préparation',
  [GeneralAssemblyStatus.VALIDATED]: 'Fiche assemblée validée',
  [GeneralAssemblyStatus.CO_OWNERS_CONVOKED]: 'Copropriétaires convoqué(e)s',
  [GeneralAssemblyStatus.OPEN]: 'Assemblée ouverte',
  [GeneralAssemblyStatus.ON_GOING]: 'Assemblée en cours',
  [GeneralAssemblyStatus.LINKS_SENT]: 'Liens envoyés',
  [GeneralAssemblyStatus.CLOSED]: 'Assemblée terminée',
};

const generalAssemblyStatusCurrent: Record<GeneralAssemblyStatus, string> = {
  [GeneralAssemblyStatus.DRAFT]: 'En préparation',
  [GeneralAssemblyStatus.VALIDATED]: 'Validation de la fiche assemblée',
  [GeneralAssemblyStatus.CO_OWNERS_CONVOKED]: 'Convocation des copropriétaires',
  [GeneralAssemblyStatus.OPEN]: 'Assemblée ouverte',
  [GeneralAssemblyStatus.ON_GOING]: 'Assemblée en cours',
  [GeneralAssemblyStatus.LINKS_SENT]: 'Liens envoyés',
  [GeneralAssemblyStatus.CLOSED]: 'Assemblée terminée',
};

const generalAssemblyStatusShortcut: Record<GeneralAssemblyStatus, string> = {
  [GeneralAssemblyStatus.DRAFT]: 'En préparation',
  [GeneralAssemblyStatus.VALIDATED]: 'Validation',
  [GeneralAssemblyStatus.CO_OWNERS_CONVOKED]: 'Convocation',
  [GeneralAssemblyStatus.OPEN]: 'Ouverture',
  [GeneralAssemblyStatus.ON_GOING]: 'En cours',
  [GeneralAssemblyStatus.LINKS_SENT]: 'Liens envoyés',
  [GeneralAssemblyStatus.CLOSED]: 'Terminé',
};

const resolutionTypes: Record<ResolutionType, string> = {
  [ResolutionType.GENERAL]: 'Générale',
  [ResolutionType.PRESIDENT]: 'Président',
  [ResolutionType.SECRETARY]: 'Secrétaire',
  [ResolutionType.COMMENT]: 'Sans vote',
  [ResolutionType.SCRUTINEER]: 'Scrutateur',
  [ResolutionType.UNION_CONCIL]: 'Conseil syndical',
};

const resolutionMajorityArticles: Record<ResolutionMajorityArticle, string> = {
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_24]:
    'Majorité article 24 - Loi du 10 juillet 1965',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_25]:
    'Majorité article 25 - Loi du 10 juillet 1965',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_26]:
    'Majorité article 26 - Loi du 10 juillet 1965',
  [ResolutionMajorityArticle.UNANIMITY]: 'Unanimité - Loi du 10 juillet 1965',
};
const resolutionMajorityArticlesLinks: Record<
  ResolutionMajorityArticle,
  string
> = {
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_24]:
    'https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000039313584/',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_25]:
    'https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000039313590/',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_26]:
    'https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000039313599/',
  [ResolutionMajorityArticle.UNANIMITY]:
    'https://www.legifrance.gouv.fr/loda/id/LEGITEXT000006068256/',
};

const resolutionMajorityArticlesShortcuts: Record<
  ResolutionMajorityArticle,
  string
> = {
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_24]: 'Art. 24',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_25]: 'Art. 25',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_26]: 'Art. 26',
  [ResolutionMajorityArticle.UNANIMITY]: 'Unanimité',
};

const resolutionMajoritySecondReading: Record<
  ResolutionMajorityArticle,
  string
> = {
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_24]: '',
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_25]:
    "L'Assemblée Générale constate que la résolution ne recueille pas la majorité de l'article 25 mais que le projet recueille au moins le tiers des voix de tous les copropriétaires présents, représentés ou ayant voté par correspondance.{br}{br}Conformément à l'article 25-1, l'Assemblée Générale peut procéder à un second vote dans les conditions de la majorité de l'article 24.{br}{br}Souhaitez-vous copier les votes par correspondance lors de la création de cette nouvelle résolution ?",
  [ResolutionMajorityArticle.MAJORITY_ARTICLE_26]:
    "L'Assemblée Générale constate que la résolution ne recueille pas la majorité de l'article 26 mais que le projet recueille l'approbation de la moitié des membres du syndicat des copropriétaires présents, représentés ou ayant voté par correspondance.{br}{br}Conformément à l'article 26-1, l'Assemblée Générale peut procéder à un second vote dans les conditions de la majorité de l'article 25.{br}{br}Souhaitez-vous copier les votes par correspondance lors de la création de cette nouvelle résolution ?",
  [ResolutionMajorityArticle.UNANIMITY]: '',
};

const voteMethods: Record<VoteMethod, string> = {
  [VoteMethod.UNKNOWN]: '-',
  [VoteMethod.EARLY_POSTAL_VOTE]: 'Vote par correspondance',
  [VoteMethod.EARLY_ONLINE_VOTE]: "Vote en ligne à l'avance",
  [VoteMethod.PHYSICALLY_PRESENT]: 'Présent physiquement',
  [VoteMethod.REMOTELY_PRESENT]: 'Présent en visioconférence',
  [VoteMethod.REPRESENTED]: 'Représenté par {representedByCoOwnerName}',
};

const voteChoices: Record<VoteChoice, string> = {
  [VoteChoice.IN_FAVOR]: 'POUR',
  [VoteChoice.AGAINST]: 'CONTRE',
  [VoteChoice.ABSTENTION]: 'ABSTENTION',
  [VoteChoice.NOT_EXPRESSED]: 'DÉFAILLANT',
};

const coOwnerEventTypes: Record<CoOwnerEventType, string> = {
  [CoOwnerEventType.PHYSICALLY_JOINED]: 'Arrivée le {date} à {time}',
  [CoOwnerEventType.PHYSICALLY_LEFT]: 'Sortie le {date} à {time}',
  [CoOwnerEventType.REMOTELY_JOINED]: 'Connexion le {date} à {time}',
  [CoOwnerEventType.REMOTELY_LEFT]: 'Déconnexion le {date} à {time}',
  [CoOwnerEventType.ANNOUNCED_REPRESENTED_BY]:
    'Don de pouvoir à {representingCoOwnerName}',
  [CoOwnerEventType.REPRESENTATIVE_ADDED]:
    'Don de pouvoir donné à {representingCoOwnerName} le {date} à {time}',
  [CoOwnerEventType.REPRESENTATIVE_REMOVED]:
    'Don de pouvoir précèdemment donné à {representingCoOwnerName}, retiré le {date} à {time}',
};

const coOwnerNotificationStatusReason: Record<
  NotificationStatusReason,
  string
> = {
  [NotificationStatusReason.MISSING_EMAIL]: 'Email inconnu',
  [NotificationStatusReason.MISSING_PHONE_NUMBER]:
    'Numéro de téléphone inconnu',
};

const coOwnerNotificationStatus: Record<NotificationStatus, string> = {
  [NotificationStatus.NOT_APPLICABLE]: 'Notification non envoyée',
  [NotificationStatus.ERROR]: 'Erreur de notification',
  [NotificationStatus.SUCCESS]: 'Notification envoyée avec succès',
};

export const fr = {
  ok: 'OK',
  cancel: 'Annuler',
  validation: {
    default: 'Ce champ est invalide',
    required: 'Ce champ est requis',
    email: 'Veuillez renseigner une adresse valide',
    invalidLength: 'Veuillez renseigner une valeur de longueur ${length}',
    invalidFormat: "La valeur renseignée n'est pas au bon format",
  },
  generalAssembly: {
    syndicName: 'Nom du Syndic',
    coOwnershipName: 'Nom de la copropriété',
    date: 'Date de convocation',
    type: 'Type',
    types: generalAssemblyTypes,
    address: 'Adresse',
    map: 'Carte',
    location: 'Lieu',
    publicNote: 'Note publique',
    publicNoteHelpText: 'Note transmise dans le mail client',
    privateNote: 'Note privée',
    privateNoteHelpText: 'Note interne',
    status: generalAssemblyStatus,
  },
  resolution: {
    title: 'Intitulé de la résolution',
    allocationKey: 'Clé de répartition',
    type: 'Type de résolution',
    types: resolutionTypes,
    candidate: 'Candidat',
    customCandidate: 'Candidat libre',
    majorityArticle: 'Article de majorité',
    majorityArticles: resolutionMajorityArticles,
    content: 'Texte de la resolution',
    comment: 'Commentaires',
    voteChoices: { ...voteChoices, [`${VoteChoice.ABSTENTION}-cut`]: 'ABST.' },
    sentence1:
      'Compte tenu du résultat de la résolution XX, cette résolution devient sans objet.',
    sentence2: "Faute de candidat, cette résolution n'est pas soumise au vote.",
    voteResultManualOverride: 'Changer le statut',
    status: {
      validated: 'Validée',
      refused: 'Refusée',
      deferred: 'Ajournée',
      notVoted: 'Non votée',
    },
  },
  module: {
    backToHome: "Retour à l'accueil",
  },
  settings: {
    title: 'Paramètres de mise en page',
    cancel: 'Annuler',
    save: 'Enregistrer',
    itemsPerRow: "Nombre d'items par ligne",
    itemsPaginationCount: 'Nombre de résultats par page',
    info: 'Paramètrer la mise en page',
    resetDefaultParameters: 'Réinitialiser',
    resetDefaultParametersInfo: 'Réinitialiser les paramètres par défaut',
  },
  pages: {
    generalAssemblies: {
      title: 'Assemblées Générales',
      list: 'Liste des assemblées générales',
      createdBy: 'Créée par',
      comingGeneralAssemblies: `À venir {generalAssembliesCount, plural,
              =0 {}
              other {({generalAssembliesCount})}}`,
      onGoingGeneralAssemblies: `En cours {generalAssembliesCount, plural,
              =0 {}
              other {({generalAssembliesCount})}}`,
      noOnGoingGeneralAssembly: 'Aucune assemblée générale en cours',
      noComingGeneralAssembly: 'Aucune assemblée générale à venir',
      pastGeneralAssemblies: `Passées {generalAssembliesCount, plural,
              =0 {}
              other {({generalAssembliesCount})}}`,
      noPastGeneralAssembly: 'Aucune assemblée générale passée',
      seeMore: 'Voir',
      creationModal: {
        createGeneralAssembly: 'Créer assemblée générale',
        title: 'Créer une assemblée générale',
        cancel: 'Annuler',
        save: 'Enregistrer',
      },
      nonAuthorizedActionTitle: 'Action non permise',
      nonAuthorizedActionSearch:
        'Vous devez entrer un minimum de 2 caractères pour une recherche valide.',
      searchInput: 'Rechercher une assemblée générale...',
      searchInformation:
        "Le nom, le lieu et l'adresse sont disponibles à la recherche. (2 caractères minimums sont requis)",
      launchHistory: {
        button: 'Stats AG',
        title: 'Historique des AG',
        thisMonth: 'Assemblées lancées ce mois-ci',
        column: {
          month: 'Mois',
          count: 'Total AG lancées',
        },
      },
      launchHistorySuperadmin: {
        button: 'Stats AG (Superadmin)',
        title: 'Historique des AG par Syndic',
        column: {
          syndic: 'Syndic',
          thisMonthWithLabel: 'Mois : {month}',
          total: 'Total',
          created: 'Créée',
          onGoing: 'Lancée',
          closed: 'Fermée',
          deleted: 'Supprimée',
        },
      },
      filter: {
        coming: 'À venir',
        onGoing: 'En cours',
        past: 'Passées',
      },
      informationsMic:
        'J’ai lu le guide d’usage et j’ai testé mon micro ainsi que mes haut-parleurs / mon casque. Ils sont correctement paramétrés et en état de fonctionnement.',
      btnConfirmMic: 'JE CONFIRME',
      audioCheckModal: {
        title: 'Test de votre micro',
        description:
          'Avant de rejoindre l’assemblée, vérifiez que votre micro est bien détecté. Parlez quelques secondes: la jauge doit réagir.',
        deviceLabel: 'Microphone détecté',
        deviceFallback: 'Micro',
        noDevice: 'Aucun microphone détecté',
        requestingPermission: 'Demande de permission micro en cours...',
        unsupported:
          'Votre navigateur ne supporte pas le test audio en temps réel.',
        error:
          'Impossible d’accéder au micro. Vérifiez la permission navigateur et réessayez.',
        signalDetected: 'Votre voix est détectée',
        noSignal: 'Nous ne détectons pas encore votre voix',
        retry: 'Réessayer',
      },
    },
    generalAssembly: {
      management: {
        title: 'Gestion',
        currentStatus: 'Étape actuelle : ',
        projectionMode: 'Mode projection',
        nextStatus: 'Prochaine étape :',
        generalAssemblyStatus: generalAssemblyStatus,
        generalAssemblyStatusCurrent: generalAssemblyStatusCurrent,
        rate: "Noter la tenue de l'assemblée",
        validate: 'Valider la fiche AG',
        settings: 'Paramètres',
        settingsCta: 'Renseigner des paramètres',
        resolutions: 'Résolutions { resolutionsCount }',
        resolutionsCreationCta: 'Créer des résolutions',
        resolutionsImportCta: 'Importer des résolutions',
        coOwners: 'Liste des copropriétaires { coOwnersCount }',
        coOwnersCta: 'Importer des copropriétaires',
        coOwnersCreationCta: 'Créer des copropriétaires',
        coOwnersImportCta: 'Importer des copropriétaires',
        validateDocuments: 'Valider les documents',
        infoValidateDocuments:
          'Le président, secrétaire et scrutateur(s) peuvent visualiser et signer les documents directement depuis leur propre compte, lorsque ceux-ci sont validés.',
        attendanceSheet: 'Feuille de présence',
        transcript: 'Procès verbal',
        deletionModal: {
          title: 'Voulez-vous vraiment supprimer cette assemblée générale ?',
          warning: 'Vous ne pourrez pas revenir en arrière',
          delete: "Supprimer l'assemblée générale",
          confirm: 'Confirmer',
          cancel: 'Annuler',
        },
        convocation: {
          postalVotes: 'Votes par correspondance',
          postalVotesCta: 'Importer des votes',
          convoke: "Envoyer le mail d'introduction",
          cancel: 'Annuler',
          confirm: 'Etes-vous sûr de vouloir envoyer le mail ?',
          warning: `Vous allez potentiellement envoyer un email à {coOwnersWithEmailCount, plural,
                    =0 {personne}
                    one {1 personne}
                    other {{coOwnersWithEmailCount} personnes}
                    }. Cette action est irréversible, vous ne pourrez plus importer ou modifier les coordonnées des copropriétaires.`,
        },
        opening: {
          open: "Ouvrir l'assemblée",
          cancel: 'Annuler',
          confirm: "Voulez-vous ouvrir l'assemblée générale ?",
          text: `Vous pourrez débuter l'émargement et les participants en ligne pourront rejoindre l'assemblée. Cependant, vous ne pourrez importer de votes par correspondance.`,
        },
        starting: {
          start: "Lancer l'assemblée",
        },
        closing: {
          confirm: 'Voulez-vous vraiment terminer cette assemblée générale ?',
          close: "Terminer l'assemblée générale",
          cancel: 'Annuler',
          warning: {
            title: "En terminant l'assemblée générale, vous ne pourrez plus :",
            openVote: 'Ouvrir de vote',
            emergeParticipants: 'Émarger des participants',
            editParticipantsList: 'Modifier la liste des participants',
            editAssemblyParameters: "Modifier les paramètres de l'assemblée",
            editOrderPaper: "Modifier l'ordre du jour",
            useProjectionMode: 'Accéder au mode projection',
          },
        },
        registrationMode: 'Mode émargement',
        manualChangeStatus: {
          generalAssemblyStatus: generalAssemblyStatus,
          demoMode: 'Démo mode',
          infoChangeStatus: 'Changer manuellement le status',
        },
        stepProgressBar: {
          generalAssemblyStatusShortcut: generalAssemblyStatusShortcut,
          generalAssemblyStatus: generalAssemblyStatus,
        },
      },

      agenda: {
        title: 'Ordre du jour',
        tantiemes: `{representedTantiemes, plural,
          =0 {Aucun tantième présent ou représenté}
          one {1 tantième présent ou représenté}
          other {{representedTantiemes} tantièmes présents ou représentés}
        } sur {tantiemesTotal} ({tantiemesFrac, number, ::scale/100 .00}%)`,
        coOwnersEarlyVotingCount: `{coOwnersEarlyVotingCount, plural,
          =0 {Aucun copropriétaire votant à l'avance}
          one {1 copropriétaire votant à l'avance}
          other {{coOwnersEarlyVotingCount} copropriétaires votants à l'avance}
        } sur {totalCoOwners} ({coOwnersFrac, number, ::scale/100 .00}%)`,
        newResolution: 'Créer résolution',

        noResolution: 'Aucune résolution renseignée',
        resolutionCreationModal: {
          title: 'Créer une résolution',
          cancel: 'Annuler',
          save: 'Enregistrer',
        },
        resolutionPostalVotesCopyModal: {
          title: 'Copier les votes par correspondance',
          modalContent:
            "Souhaitez-vous copier les votes par correspondance sur cette nouvelle résolution ? Il est conseillé de le faire lors d'une seconde lecture.",
          copyVotes: 'Oui',
          doNotCopyVotes: 'Non',
          cancel: 'Annuler',
        },
        resolutionEditionModal: {
          save: 'Enregistrer',
          cancel: 'Annuler',
        },
        subResolutionCreationModal: {
          createSubResolution: 'Créer sous-résolution',
          cancel: 'Annuler',
        },
        resolutionResultsModal: {
          ok: 'Ok',
          cancel: 'Annuler',
          results: 'Résultats',
          all: 'Tous',
          inFavor: 'Pour',
          against: 'Contre',
          abstention: 'Abstentions',
          notExpressed: 'Défaillants',
        },
        voteResultsList: {
          name: 'Nom',
          coOwnerId: 'Copropriétaire ID',
          voteMethod: 'Méthode de vote',
          tantiemes: 'Tantièmes',
          voteChoice: 'Vote',
          total: 'Total',
          [VoteMethod.EARLY_POSTAL_VOTE]: 'Par correspondance',
          [VoteMethod.EARLY_ONLINE_VOTE]: "En ligne à l'avance",
          [VoteMethod.PHYSICALLY_PRESENT]: 'Présent physiquement',
          [VoteMethod.REMOTELY_PRESENT]: 'Présent en visioconférence',
          [VoteMethod.REPRESENTED]: 'Représenté par {representedByCoOwnerName}',
          [VoteMethod.UNKNOWN]: '-',
          representedByFilter: 'Représenté par',
          voteChoices: voteChoices,
        },
        resolutionResultsGraph: {
          label:
            '{tantiemes} tant. ({tantiemesFrac, number, ::scale/100 .00}%)',
          [ResolutionVoteResult.ACCEPTED]: 'Adoptée',
          [ResolutionVoteResult.REFUSED]: 'Rejetée',
          [VotePresency.VOTING]: 'Présents',
          [VotePresency.NOT_VOTING]: 'Absents',
          [VoteChoice.IN_FAVOR]: 'Pour',
          [VoteChoice.AGAINST]: 'Contre',
          [VoteChoice.ABSTENTION]: 'Abstentions',
          [VoteChoice.NOT_EXPRESSED]: 'Défaillants',
          nonVoted: 'Non voté',
        },
        resolutionCard: {
          ok: 'Ok',
          cancel: 'Annuler',
          allocationKey: 'Clé de répartition : ',
          editResolution: 'Modifier la résolution',
          createSubResolution: 'Créer une sous-résolution',
          cloneResolution: 'Dupliquer la résolution',
          deleteResolution: 'Supprimer la résolution',
          copyVotesResults: "Copier les votes d'une précédente résolution",
          confirmResolutionDeletion:
            'Voulez-vous vraiment supprimer la résolution ? Tous les votes associés,{br}y compris par correspondance seront supprimés également.',
          quorumReached: 'Le quorum est atteint',
          quorumNotReached: "Le quorum n'est pas atteint",
          quorumNotReachedButPossible:
            "Le quorum n'est pas atteint, mais un vote en seconde lecture est possible",
          quorumNotExisting: 'Sans quorum',
          refused: 'refusée',
          accepted: 'adoptée',
          displayResults: 'Afficher les résultats',
          reinitializeVotes: 'Réinitialiser les votes',
          confirmReinitializeVotes:
            'Voulez-vous vraiment réinitialiser les votes ? Les votes par correspondance ne sont pas concernés.',
          validated: 'validée',
          opened: 'En cours',
          waiting: 'En attente',
          noVotesToCopy:
            "Il n'y a aucuns votes à copier. Vérifiez les clés de répartition.",
          unknown: 'Inconnu',
          deferred: 'Ajournée',
          not_voted: 'Non votée',
        },
        copyVotesResults: {
          title: "Copier les résultats d'une résolution déjà votée",
          modalContent:
            'Souhaitez-vous aussi copier les votes par correspondance ? {icon}',
          modalWarning:
            'Si la résolution destinataire contient déjà des votes par correspondance, ils seront écrasés.',
          copyVotes: 'Tout copier',
          doNotCopyPostalVotes: 'Copier sans VPC',
          cancel: 'Annuler',
          chooseResolution: 'Choisir une résolution à copier',
          resolutionLabel: 'Résolution',
          resolutionNumber: 'N°',
          resolutionRequired: 'Vous devez sélectionner une résolution',
        },
        resolutionsHeader: {
          inFavor: 'Pour',
          against: 'Contre',
          abstention: 'Abs.',
          notExpressed: 'Défaillants',
          resolution: 'Résolution',
          number: 'N°',
          status: 'Statut',
        },
        deletingModal: {
          deleteAll: 'Tout supprimer',
          canNotDelete:
            "Cett action n'est pas disponible lorsque l'assemblée a déjà commencé",
          deleteInfo: 'Supprimer toutes les résolutions',
          confirmDeletion:
            'Voulez-vous vraiment supprimer toutes les résolutions ?',
        },
      },
      coOwners: {
        title: 'Copropriétaires',
        list: 'Liste des copropriétaires',
        searchInput: 'Rechercher un copropriétaire...',
        searchInformation:
          "Seul le nom et l'adresse sont disponibles à la recherche. (2 caractères minimums sont requis)",
        tantiemes: `{representedCoOwnersCount, plural,
          =0 {Aucun copropriétaire présent ou représenté}
          one {1 copropriétaire présent ou représenté}
          other {{representedCoOwnersCount} copropriétaires présents ou représentés}
        } totalisant {representedTantiemes} {representedTantiemes, plural,
          =0 {tantième}
          one {tantième}
          other {tantièmes}} sur {tantiemesTotal} ({tantiemesFrac, number, ::scale/100 .00}%)`,
        coOwnersEarlyVotingCount: `{coOwnersEarlyVotingCount, plural, 
          =0 {Aucun copropriétaire votant}
          one {1 copropriétaire votant}
          other {{coOwnersEarlyVotingCount} copropriétaires votants}
        } à l'avance sur {totalCoOwners} ({coOwnersFrac, number, ::scale/100 .00}%)`,
        coOwnersWithFailedConvocations: `{coOwnersWithFailedConvocationsCount, plural,
          =0 {N/A}
          one {1 copropriétaire n'a pas pu recevoir l'email de convocation}
          other {{coOwnersWithFailedConvocationsCount} copropriétaires n'ont pas pu recevoir l'email de convocation}
        }:`,
        noCoOwner: 'Aucun copropriétaire',
        coOwnerCard: {
          voteMethods: voteMethods,
          externalRepresentative: 'Mandataire extérieur',
          seeProfile: 'Voir le profil',
          editContactInfos: 'Modifier le profil',
          deleteCoOwner: 'Supprimer le copropriétaire',
          confirmDeleteCoOwner:
            'Voulez-vous vraiment supprimer le copropriétaire ?',
          okText: 'Oui',
          cancelText: 'Non',
        },
        coOwnerProfileModal: {
          coOwnerId: 'Copropriétaire ID',
          coOwnerIdAndLoginCode: 'Copropriétaire ID - Numéro de badge',
          convocationStatus: {
            title: 'Email de convocation',
            convoked: 'Convoqué avec succès',
            reason: coOwnerNotificationStatusReason,
          },
          status: 'Statut',
          announcedVoteMethod: 'Annoncé : ',
          lastDetectedVoteMethod: 'Dernièrement : ',
          realTimeVoteMethod: 'En cours : ',
          voteMethods: voteMethods,
          events: {
            title: 'Historique',
            notificationStatus: coOwnerNotificationStatus,
            notificationStatusReason: {
              [NotificationStatusReason.MISSING_EMAIL]: 'Email inconnu',
            },
            coOwnerEventTypes: coOwnerEventTypes,
          },
          address: 'Adresse',
          votes: 'Vote(s)',
          deleteAllVotes: 'Supprimer tous les votes',
          deleteSelectedVotes: 'Supprimer les votes sélectionnés',
          resolutionsChoice: 'Choix des résolutions',
          resolution: 'Résolution {resolutionNumber} - {title}',
          votedResolution: 'Résolution {resolutionNumber} - {title} : {choice}',
          keys: 'Clé(s)',
          lots: 'Lot(s) : ',
          lot: 'Lot',
          tantiemes: 'Tantièmes : ',
          reConvokeModal: {
            tooltip: "Renvoyer l'email de convocation",
            email: 'Adresse email',
            phoneNumber: 'Numéro de téléphone',
            reConvoke: "Renvoyer l'email de convocation",
          },
          removePostalVoteMethodTitle:
            'Voulez-vous vraiment supprimer le statut ? Les votes par correspondance seront également supprimés.',
          removePostalVoteMethodTooltip: 'Supprimer le statut',
          createPostalVoteMethodTitle:
            'Voulez-vous vraiment ajouter le status votant par correspondance ?',
          createPostalVoteMethodTooltip:
            'Ajouter le statut votant par correspodance',
          okText: 'Oui',
          cancelText: 'Non',
          formName: {
            submit: 'Valider',
            cancel: 'Annuler',
            requiredField: 'Champs obligatoire',
          },
        },
        coOwnerEditionModal: {
          title: 'Modifier le profil',
          update: 'Mettre à jour',
          cancel: 'Annuler',
          successUpdated: 'Le profil a bien été mis à jour',
        },
        creationModal: {
          createCoOwner: 'Créer copropriétaire',
          canNotCreateCoOwner:
            "Vous ne pouvez pas créer de copropriétaire lorsque l'assemblée a déjà commencé",
          title: 'Créer un copropriétaire',
          cancel: 'Annuler',
          save: 'Enregistrer',
          address: 'Adresse n°1',
          addressBis: 'Adresse n°2',
          addressTer: 'Adresse n°3',
          name: 'Prénom / Nom',
          civility: 'Civilité',
          male: 'Monsieur',
          female: 'Madame',
          country: 'Pays',
          lots: 'Lots',
          postalCode: 'Code postal',
          coOwnerId: 'Copropriétaire ID',
          city: 'Ville',
          defaultCountry: 'France',
          email: 'Email',
          phoneNumber: 'Numéro de téléphone',
          addLot: 'Ajouter un lot',
          allocationKeyId: 'Clé de répartition',
          lotId: 'Lot ID',
          tantiemes: 'Tantièmes',
          identity: 'Identité',
          postalAddress: 'Adresse',
          contact: 'Contact',
          successCreated: 'Le copropriétaire a bien été créé',
          deleteLotInfo: 'Supprimer le lot',
        },
        coOwnerVotesModal: {
          modalTitle: 'Votes',
          votesInfo: 'Voir les votes',
          resolutionTitle: 'Résolution',
          submit: 'Valider',
          cancel: 'Annuler',
          inFavor: 'Pour',
          against: 'Contre',
          abstention: 'Abstention',
          notExpressed: 'Défaillant',
          voteDisabled: 'Sans vote',
          voteInProgress: 'Vote en cours ou fermé',
          successSaved: 'Les votes ont bien été pris en compte',
          notEarlyVotingCoOwner:
            'Éditable uniquement par les votants par correspondance',
          missingAllocationKey:
            'Auncuns lots concernant cette clé de répartition.',
          selectAll: 'Tout sélectionner',
          majorityArticles: resolutionMajorityArticles,
          totalByChoice: 'Total par choix',
          total: 'Total',
        },
        debuggingModal: {
          debugCoOwners: 'Corriger les erreurs',
          debuggingModal: {
            title: 'Anomalies détectées',
            description: `Il semblerait que les méthodes de vote publiques des copropriétaires suivants ne correspondent pas avec leur réelle méthode de vote (ils n'apparaissent sans doute pas comme présents alors qu'ils le sont) :`,
            confirmCoOwnersDebugging:
              'Voulez-vous rétablir les bonnes méthodes de vote ?',
            buttonOk: 'Corriger',
          },
        },
        deletingModal: {
          deleteCoOwners: 'Tout supprimer',
          canNotDeleteCoOwners:
            "Vous ne pouvez pas supprimer de copropriétaire lorsque l'assemblée a déjà commencé",

          deleteCoOwnersInfo: 'Supprimer tous les copropriétaires',
          confirmCoOwnersDeletion:
            'Voulez-vous vraiment supprimer tous les copropriétaires ?',
        },
      },
      allocationKeys: {
        title: 'Clés de répartition',
        list: 'Liste des clés de répartition',
        searchInput: 'Rechercher une clé...',
        searchInformation:
          "Seul le nom et l'adresse sont disponibles à la recherche. (2 caractères minimums sont requis)",
        noAllocationKey: 'Aucune clé',
        allocationKeyCard: {
          primaryAllocationKey: 'Clé primaire',
          tantiemes: 'Tantièmes',
          lotsCount: 'Lots',
          coOwnersCount: 'Copropriétaires',
          allocationKeysCount: 'Clés de répartition',
          seeProfile: 'Voir les clés',
          editContactInfos: 'Modifier les clés',
          deleteAllocationKey: 'Supprimer la clé',
          confirmDeleteAllocationKey:
            'Voulez-vous vraiment supprimer la clé de répartition ?',
          okText: 'Oui',
          cancelText: 'Non',
        },
        coOwnersModal: {
          title: 'Liste des copropriétaires',
          info: 'Voir les copropriétaires',
          coOwnerColumn: 'Copropriétaire',
          tantiemesColumn: 'Tantièmes',
          lotsColumn: 'Lots',
          total: 'Total',
        },
        creationModal: {
          create: 'Créer clé',
          canNotCreate:
            "Vous ne pouvez pas créer de clé de répartition lorsque l'assemblée a déjà commencé",
          title: 'Créer une clé de répartition',
          cancel: 'Annuler',
          save: 'Enregistrer',
          addLot: 'Ajouter un lot',
          name: 'Nom',
          allocationKeyId: 'ID',
          isPrimaryKey: 'Clé primaire',
          lotId: 'Lot ID',
          tantiemes: 'Tantièmes',
          successCreated: 'La clé de répartition a bien été créée',
          deleteLotInfo: 'Supprimer le lot',
          existingAllocationKeyId: 'Cet ID de clé existe déjà',
        },
        deletingModal: {
          deleteAll: 'Tout supprimer',
          canNotDelete:
            "Vous ne pouvez pas supprimer de clés de répartition lorsque l'assemblée a déjà commencé",

          deleteInfo: 'Supprimer toutes les clés de répartition',
          confirmDeletion:
            'Voulez-vous vraiment supprimer toutes les clés de répartition ?',
        },
        editionModal: {
          title: 'Modifier la clé',
          update: 'Mettre à jour',
          edit: 'Modifier la clé',
          cancel: 'Annuler',
          successUpdated: 'La clé de répartition a bien été mise à jour',
        },
      },
      settings: {
        title: 'Paramètres',
        basicInformation: {
          title: 'Informations de base',
        },
        system: {
          title: 'Système',
          isTest: 'Mode démo',
          isTestHelpText: 'Permet de changer modifier manuellement les status',
          isRemoteOnly: 'Seulement en visioconférence',
          cap: 'Plafond :',
          representationPercentage: '% de la copropriété',
          representationCount: 'pouvoirs',
        },
        transcriptManagement: {
          title: 'Procès Verbal',
          isTranscriptManagementHelperText:
            "Le Procès verbal ne doit pas encore être validé pour que les paramètres ci-dessous s'opèrent. Si tel est le cas, il est nécessaire de le réinitialiser. Dans ce cas vos modifications seront perdues.",
          doesTranscriptDisplaysOnlyObjectors:
            'Afficher uniquement les opposants',
          transcriptResolutionBackgroundColor:
            'Couleur de fonds des blocs de résolution',
          transcriptEvenColor: 'Résolutions paires',
          transcriptOddColor: 'Résolutions impaires',
          transcriptFixedModal:
            "Afficher l'édition du procès verbal dans une fenêtre fixe",
          transcriptDisplayCoOwners:
            "Afficher le nom des copropriétaires avant le début de l'assemblée",
          doesTranscriptDisplayElectionCoOwnerName:
            'Afficher le nom du copropriétaire se présentant à une élection à la suite du contenu des résolutions',
          presentCoOwners: 'Présents',
          missingCoOwners: 'Absents',
        },
        save: 'Enregistrer',
        settingsSaved: 'Paramètres enregistrés',
        leaveWithoutSavingAlertMessage:
          "Certaines modifications n'ont pas été sauvegardées, êtes-vous sûr de vouloir quitter cette page ?",
      },
      imports: {
        title: 'Imports',
        donwloadResolutionsSample:
          "Télécharger le modèle d'import de résolutions",
        donwloadVotesSample: "Télécharger le modèle d'import de votes",
        donwloadCoOwnersSample:
          "Télécharger le modèle d'import de copropriétaires VOTIM",
        donwloadCoOwnersSampleNeoteem:
          "Télécharger le modèle d'import de copropriétaires  Neoteem",
        coOwnersWarning:
          'Si cette notification ne disparaît pas, cela signifie que l\'importation des copropriétaires est incomplète, veuillez les réimporter.{br}Si le problème persiste, supprimer les copropriétaires manuellement depuis la page "Détails" et réimporter.',
        bellman: {
          title: 'Import avec un fichier Bellman ou Powimmo',
          helpText:
            "Importer d'un coup toutes les information de votre assemblée générale, y compris les lots, les copropriétaires et les résolutions.",
          bellman: 'Bellman',
          importModal: {
            title: 'Importer votre fichier Bellman',
            closedImportHelpText:
              "L'import de copropriétaires n'est plus possible une fois que les copropriétaires ont été convoqués",
            addFile: 'Cliquez ici ou déposer un fichier directement',
            changeFile: '(changer de fichier en cliquant ici)',
            fixErrorsBeforeSubmit: `Corriger {errorsNumber, plural,
              one {l'erreur}
              other {les erreurs}} avant d'importer`,
            import: 'Importer',
          },
        },
        donwloadBellmanSample: 'Télécharger modèle Powimmo ',
        powimmo: {
          button: 'Powimmo',
          importModal: {
            title: 'Importer votre fichier Powimmo',
            addFile: 'Cliquez ici ou déposer un fichier directement',
            changeFile: '(changer de fichier en cliquant ici)',
            import: 'Importer',
          },
        },
        bellmanAndPowimmo: {
          title: 'Import avec un fichier Bellman ou Powimmo',
          helpText:
            "Importer d'un coup toutes les information de votre assemblée générale, y compris les lots, les copropriétaires et les résolutions.",
        },
        coOwnersList: {
          title: 'Liste des copropriétaires',
          helpText: "Veuillez sélectionner l'origine de votre fichier d'import",
          votim: 'Votim',
          thetrawin: 'Thetrawin',
          ics: 'ICS',
          neoteem: 'Neoteem',
          powimmo: 'Powimmo',
          importModal: {
            title: 'Importer au format {format}',
            closedImportHelpText:
              "L'import de copropriétaires n'est plus possible une fois que les copropriétaires ont été convoqués",
            addFile: 'Cliquez ici ou déposer un fichier directement',
            changeFile: '(changer de fichier en cliquant ici)',
            extractedData: 'Données extraites',
            coOwnersCount: `{coOwnersCount, plural,
              =0 {Aucun copropriétaire trouvé}
              one {1 copropriétaire trouvé}
              other {{coOwnersCount} copropriétaires trouvés}}`,
            lotsCount: `{lotsCount, plural,
              =0 {Aucun lot trouvé}
              one {1 lot trouvé}
              other {{lotsCount} lots trouvés}}`,
            allocationKeysCount: `{allocationKeysCount, plural,
              =0 {Aucune clé de répartition trouvée}
              one {1 clé de répartition trouvée ({tantiemesTotal} tantièmes au total)}
              other {{allocationKeysCount} clés de répartition trouvées ({tantiemesTotal} tantièmes au total)}}`,
            tantiemes: `{tantiemes, plural,
              =0 {Aucun tantième trouvé}
              one {1 tantième trouvé}
              other {{tantiemes} tantièmes trouvés}} sur {tantiemesTotal} ({tantiemesFrac, number, ::scale/100 .00}%)`,
            isPrimaryKey: 'clé primaire',
            errors: {
              title: `{errorsNumber, plural,
                one {Erreur repérée}
                other {Erreurs repérées}}`,
              field_missing: `Ligne {row} : le champ "{field}" est requis`,
              invalid_type: `Ligne {row} : le champ "{field}" doit être un nombre`,
              field_invalid: `Ligne {row} : le champ "{field}" n'est pas valide`,
              invalid_entry: `Ligne {row} : le champ "{field}" de valeur {value} n'est pas valide.`,
              duplicate_email: `Ligne {row} : le copropriétaire "{value}" ne peut avoir qu'une adresse email valide. (Bloquant)`,
              duplicate_co_owner: `Ligne {row} : les copropriétaires "{value}" ont la même adresse email. (Bloquant)`,
              [RowType.CO_OWNER]: {
                coOwnerId: 'Copropriétaire ID',
                civility: 'Désignation du copropriétaire',
                name: 'Nom du copropriétaire',
                address: 'Adresse du copropriétaire',
                postalCode: 'Code postal du copropriétaire',
                city: 'Ville du copropriétaire',
                country: 'Pays du copropriétaire',
                lotId: 'ID du lot',
                allocationKeyId: 'ID de la clé de répartition associée',
                tantiemes: 'Tantièmes du lot',
                email: 'Adresse email',
              },
              [RowType.ALLOCATION_KEY]: {
                allocationKeyId: 'ID de la clé de répartition',
                name: 'Nom de la clé de répartition',
                tantiemes: 'Tantièmes de la clé de répartition',
                tantiemesTotal: 'Total des tantièmes',
              },
              NONE: {
                postalCode: 'Code postal',
                email: 'Email',
              },
              bellman: {
                field_missing: `{value} : le champ "{field}" est requis`,
                invalid_type: `{value} : le champ "{field}" doit être un nombre`,
                field_invalid: `{value} : le champ "{field}" n'est pas valide`,
                invalid_entry: `{value} : le champ "{field}" de valeur {value} n'est pas valide.`,
                duplicate_email: `{value} : le copropriétaire "{value}" ne peut avoir qu'une adresse email valide. (Bloquant)`,
                duplicate_co_owner: `{value} : les copropriétaires "{value}" ont la même adresse email. (Bloquant)`,
              },
            },
            import: 'Importer',
            fixErrorsBeforeImport: `Résoudre {errorsNumber, plural,
                one {l'erreur bloquante}
                other {les erreurs bloquantes}}`,
            importAnyway: `Importer malgré {errorsNumber, plural,
                one {l'erreur}
                other {les erreurs}}`,
          },
        },
        postalVotes: {
          title: 'Votes par correspondance',
          importModal: {
            title: 'Importer des votes par correspondance',
            disabledImportHelpText:
              "L'assemblée générale doit être validée pour pouvoir importer des votes par correspondance",
            closedImportHelpText:
              "L'import de votes par correspondance n'est plus possible une fois que l'assemblée générale a été ouverte",
            addFile: 'Cliquez ici ou déposer un fichier directement',
            changeFile: '(changer de fichier en cliquant ici)',
            extractedData: 'Données extraites',
            coOwnersCount: `{coOwnersCount, plural,
              =0 {Aucun copropriétaire votant par correspondance trouvé}
              one {1 copropriétaire votant par correspondance trouvé}
              other {{coOwnersCount} copropriétaires votants par correspondance trouvés}}`,
            votedResolutionsCount: `{votedResolutionsCount, plural,
              =0 {Aucune résolution votée}
              one {1 résolution votée}
              other {{votedResolutionsCount} résolutions votées}}`,
            errors: {
              title: `{errorsNumber, plural,
                one {Erreur repérée}
                other {Erreurs repérées}}`,
              field_missing: `Colonne {col} : le champ "{field}" est requis.`,
              field_invalid: `Colonne {col} : la valeur "{value}" est invalide pour le champ "{field}".`,
              duplicate_entry: `Colonne {col} : le champ "{field}" d'ID {value} est en double.`,
              missing_entry:
                'Colonne {col} : la résolution {value} est manquante.',
              invalid_entry: `Colonne {col} : le champ "{field}" de valeur {value} n'est pas valide.`,
              resolutionNumber: 'Numéro de résolution',
              coOwnerId: 'Copropriétaire ID',
              coOwner: 'Copropriétaire',
              date: 'Date',
              choice: 'Choix du copropriétaire',
              invalid_allocation_key: `Colonne {col}, résolution n° {value} : le copropriétaire n'a pas de lots sur cette clé de répartition.`,
              invalid_resolution: `Colonne {col}, résolution n° {value} : la résolution est de type commentaire ou n'existe pas.`,
              bellman: {
                field_missing: `Le champ "{field}" est requis.`,
                field_invalid: `La valeur "{value}" est invalide pour le champ "{field}".`,
                duplicate_entry: `Le champ "{field}" d'ID {value} est en double.`,
                missing_entry: 'La résolution {value} est manquante.',
                invalid_entry: `Le champ "{field}" de valeur {value} n'est pas valide.`,
                invalid_allocation_key: `Résolution n° {value} : le copropriétaire n'a pas de lots sur cette clé de répartition.`,
                invalid_resolution: `Résolution n° {value} : la résolution est de type commentaire ou n'existe pas.`,
              },
            },
            votimButton: 'Votim',
            neoteemButton: 'Neoteem',
            importAnyway: `Importer malgré {errorsNumber, plural,
                one {l'erreur}
                other {les erreurs}}`,
            importModal: {
              errors: {
                invalid_entry: `Colonne {col} : le champ "{field}" de valeur {value} n'est pas valide.`,
                invalid_resolution: `Colonne {col}, résolution n° {value} : la résolution est de type commentaire ou n'existe pas.`,
                duplicate_entry: `Colonne {col} : le champ "{field}" d'ID {value} est en double.`,
                missing_entry:
                  'Colonne {col} : la résolution {value} est manquante.',
                coOwnerId: 'Copropriétaire ID',
                resolutionNumber: 'Numéro de résolution',
                date: 'Date',
                choice: 'Choix du copropriétaire',
              },
            },
          },
        },
        resolutions: {
          title: 'Résolutions',
          importModal: {
            import: 'Importer',
            title: 'Importer des résolutions',
            addFile: 'Cliquez ici ou déposer un fichier directement',
            changeFile: '(changer de fichier en cliquant ici)',
            disabledImportHelpText:
              "Il ne doit pas exister de résolutions avant d'en importer.",
            fixErrorsBeforeSubmit: `Corriger {errorsNumber, plural,
                one {l'erreur}
                other {les erreurs}} avant d'importer`,
            resolutionsCount: `{resolutionsCount, plural,
              =0 {Aucune résolution trouvée}
              one {1 résolution trouvée}
              other {{resolutionsCount} résolutions trouvées}}`,
            extractedData: 'Données extraites',
            errors: {
              title: `{errorsNumber, plural,
                one {Erreur repérée}
                other {Erreurs repérées}}`,
              duplicate_entry: `Ligne {row} / Colonne {col} : le champ "{field}" d'ID "{value}" est en double.`,
              missing_entry: `Ligne {row} / Colonne {col} : le champ "{field}" de valeur "{value}" est manquant.`,
              invalid_entry: `Ligne {row} / Colonne {col} : le champ "{field}" de valeur "{value}" n'est pas valide.`,
              majorityArticle: 'Majorité',
              allocationKey: 'Clé de répartition',
              resolutionTitle: 'Titre',
              candidatingCoOwnerId: 'Candidat ID',
              customCandidate: 'Candidat Hors assemblée',
              resolutionNumber: 'Numéro',
              content: 'Contenu',
              bellman: {
                duplicate_entry: `[ Résolution id : {resolutionId} - Titre : "{resolutionTitle}"] : le champ "{field}" d'ID "{value}" est en double.`,
                missing_entry: `[ Résolution id : {resolutionId} - Titre : "{resolutionTitle}"] : le champ "{field}" de valeur "{value}" est manquant.`,
                invalid_entry: `[ Résolution id : {resolutionId} - Titre : "{resolutionTitle}"] : le champ "{field}" de valeur "{value}" n'est pas valide.`,
              },
              type: 'Type de résolution',
              resolutionId: 'ID de résolution',
            },
            closedImportHelpText:
              "L'import de résplutions n'est plus possible une fois que les copropriétaires ont été convoqués",
          },
        },
      },
      documents: {
        title: 'Documents',
        helpText: 'Ci-dessous la liste des documents téléchargeables',
        transcript: {
          button: 'Procès Verbal',
          save: 'Enregistrer',
          saveSuccess: 'Procès verbal sauvegardé',
          print: 'Imprimer',
          cancel: 'Annuler',
          reset: 'Réinitialiser',
          validate: 'Valider',
          validateSuccess: 'Procès verbal validé',
          requiredValidation:
            'Le procès verbal doit être validé avant de pouvoir exécuter cette action.',
          pdfTitle: 'PV-{residence}-{date}',
          resetSuccess: 'PV réinitialisé',
          resetTooltip: `Revenir au procès verbal d'origine`,
          doNotDelete: 'Ne pas supprimer ce bloc',
          resetConfirm:
            'Voulez-vous vraiment réinitialiser le PV ?{br}Cette action est irréversible.',
          ok: 'Oui',
          nok: 'Non',
          residence: 'Résidence : ',
          title: "Procès verbal{br}de l'assemblée générale du {date}",
          introduction:
            "Le <b>{date}</b> à <b>{time}</b> les copropriétaires de l'immeuble, Résidence <b>{residence}</b> se sont réunis en Assemblée Générale sur convocation régulièrement adressée par le syndic <b>{syndic}</b>, à tous les copropriétaires.{br}{br}Il a été dressé une feuille de présence qui a été signée par tous les copropriétaires présents et par les mandataires qui s'étaient faits représenter soit : <b>{tantiemesPresent} / {totalTantiemes}</b> tantièmes.",
          missingCoOwnersList:
            "Copropriétaires ABSENTS et NON REPRÉSENTÉS avant l'ouverture des votes :",
          presentCoOwnersList:
            "Copropriétaires PRÉSENTS, REPRÉSENTÉS et VOTANTS PAR CORRESPONDANCE avant l'ouverture des votes :",
          missingCoOwnersTantiemes:
            'Soit {missingCoOwnersCount} / {totalCoOwners} absents totalisant {missingCoOwnersTantiemes} / {totalTantiemes} tantièmes.',
          presentCoOwnersTantiemes:
            'Soit {presentCoOwnersCount} / {totalCoOwners} absents totalisant {presentCoOwnersTantiemes} / {totalTantiemes} tantièmes.',
          availableDocuments:
            "Le représentant du syndic dépose sur le bureau à disposition de l'Assemblée : ",
          coOwnerShipRegulations: 'Le règlement de copropriété',
          convocationsBook: 'Le livre des convocations à la présente Assemblée',
          deliberationsRegister: 'Le registre des délibérations',
          openingAssembly:
            "Le président déclare la séance ouverte et rappelle l'ordre du jour sur lequel l'Assemblée est appelée à délibérer.{br}{br}Les résolutions apparaissent dans l'ordre dans lequel elles ont été abordées durant l'Assemblée Générale.",
          closingAssembly:
            "Aux termes des opérations qui précèdent, l'ordre du jour se trouvant épuisé et personne ne demandant plus la parole, le Président déclare l'Assemblée close et la séance est levée le {closedDate} à {closedHour}.",
          legalDispositions: {
            title: 'Dispositions légales',
            content:
              "Les dispositions légales actuellement en vigueur nous obligent à notifier en la forme recommandée avec accusé de réception, le présent procès-verbal, aux copropriétaires qui n'ont été ni présents, ni représentés à l'assemblée générale, ainsi qu'aux copropriétaires opposants à l'une ou plusieurs des résolutions adoptées ou refusées par l'assemblée générale.{br}{br}<b>Article 42 de la loi du 10 juillet 1965 modifié :</b>{br}{br}Les dispositions de l'article 2224 du code civil relatives au délai de prescription et à son point de départ sont applicables aux actions personnelles relatives à la copropriété entre copropriétaires ou entre un copropriétaire et le syndicat.{br}{br}Les actions en contestation des décisions des assemblées générales doivent, à peine de déchéance, être introduites par les copropriétaires opposants ou défaillants dans un délai de deux mois à compter de la notification du procès-verbal d'assemblée, sans ses annexes. Cette notification est réalisée par le syndic dans le délai d'un mois à compter de la tenue de l'assemblée générale.{br}{br}Sauf urgence, l'exécution par le syndic des travaux décidés par l'assemblée générale en application des articles 25 et 26 de la présente loi est suspendue jusqu'à l'expiration du délai de deux mois mentionné au deuxième alinéa du présent article.{br}{br}S'il est fait droit à une action contestant une décision d'assemblée générale portant modification de la répartition des charges, le tribunal judiciaire procède à la nouvelle répartition. Il en est de même en ce qui concerne les répartitions votées en application de l'article 30.{br}{br}<b>Article 10 du décret du 17 mars 1967 modifié :</b>{br}{br}A tout moment, un ou plusieurs copropriétaires, ou le conseil syndical, peuvent notifier au syndic la ou les questions dont ils demandent qu'elles soient inscrites à l'ordre du jour d'une assemblée générale. Le syndic porte ces questions à l'ordre du jour de la convocation de la prochaine assemblée générale. Toutefois, si la ou les questions notifiées ne peuvent être inscrites à cette assemblée compte tenu de la date de réception de la demande par le syndic, elles le sont à l'assemblée suivante.{br}{br}Lorsque la convocation de l'assemblée générale est sollicitée en application de l'article 17-1 AA de la loi du 10 juillet 1965, le syndic ne porte à l'ordre du jour de cette assemblée que les questions relatives aux droits et obligations du ou des copropriétaires demandeurs.{br}{br}Le ou les copropriétaires ou le conseil syndical qui demandent l'inscription d'une question à l'ordre du jour notifient au syndic, avec leur demande, le projet de résolution lorsque cette notification est requise en application des 7° et 8° du I de l'article 11. Lorsque le projet de résolution porte sur l'application du b de l'article 25 de la loi du 10 juillet 1965, il est accompagné d'un document précisant l'implantation et la consistance des travaux.{br}{br}Le syndic rappelle les dispositions du présent article sur les appels de fonds qu'il adresse aux copropriétaires.",
          },
          sign: 'Signer',
          clear: 'Recommencer',
          singatureSuccess: 'Votre signature a bien été prise en compte',
          signatureTitle:
            'Signature du procès verbal par le secrétaire {coOwnerName}',
          signatureError: 'Signature non valide',
          signaturePrerequisites: 'Merci de faire une signature plus grande.',
          resolution: {
            agenda: 'Ordre du jour',
            majorityArticles: resolutionMajorityArticles,
            title: 'Résolution',
            vote: {
              inFavor: 'Votes pour',
              against: 'Votes contre',
              abstention: 'Abstentions',
              notVoting: 'Absents',
              notExpressed: 'Défaillants',
              total: 'Soit un total',
            },
            tantiemesFrac: `{tantiemesFrac, plural,
            =0 {ème}
            one {ème}
            other {èmes}}`,
            refused: 'La résolution est rejetée.',
            accepted: 'La résolution est adoptée.',
            deferred: 'Résolution ajournée.',
            not_voted: 'Résoltuion non votée.',
            firstReadingRejectedByArt25:
              "L'Assemblée Générale constate que la résolution ne recueille pas la majorité de l'article 25 mais que le projet recueille au moins le tiers des voix de tous les copropriétaires.",
            secondReadingByArt25:
              "Conformément à l'article 25-1, l'Assemblée Générale procède immédiatement à un second vote dans les conditions de la majorité de l'article 24.",
            firstReadingRejectedByArt26:
              "L'Assemblée Générale constate que la résolution ne recueille pas la majorité de l'article 26 mais que le projet recueille au moins l'approbation de la moitié des membres du syndicat des copropriétaires présents, représentés ou ayant voté par correspondance, représentant au moins le tiers de tous les copropriétaires.",
            secondReadingByArt26:
              "Conformément à l'article 26-1, l'Assemblée Générale procède immédiatement à un second vote dans les conditions de la majorité de l'article 25.",
            warningVotingResults:
              'Un problème est survenu concernant cette résolution, vérifiez les informations des copropriétaires et les clé de répartitions.',
            election: {
              electionAccepted: 'Au vu des résultats, {coOwner} est ',
              electionRefused: "Au vu des résultats, {coOwner} n'est pas ",
              electionDeferred: ' ',
              electionNot_voted: ' ',
              electionAcceptedCustomCandidate:
                'Au vu des résultats, le candidat {coOwner} est ',
              electionRefusedCustomCandidate:
                "Au vu des résultats, le candidat {coOwner} n'est pas ",
              manElected: 'élu',
              womanElected: 'élue',
              manScrutineer: 'scrutateur de séance.',
              womanScrutineer: 'scrutatrice de séance.',
              manPresident: 'président de séance.',
              womanPresident: 'présidente de séance.',
              manSecretary: 'secrétaire de séance.',
              womanSecretary: 'secrétaire de séance.',
              manUnionConcil: 'membre du conseil syndical.',
              womanUnionConcil: 'membre du conseil syndical.',
            },
            incomingCoOwners: `{incomingCoOwnersCount, plural,
            one {Arrivée}
            other {Arrivées}}`,
            leavingCoOwners: `{leavingCoOwnersCount, plural,
            one {Départ}
            other {Départs}}`,
            comment: 'Commentaire',
          },
          signature: {
            president: 'LE PRÉSIDENT',
            scrutineers: `{scrutineersCount, plural,
                one {LE SCRUTATEUR}
                other {LES SCRUTATEURS}}`,
            secretary: 'LE SECRÉTAIRE',
          },
          removeSignature: 'Retirer la signature',
          confirmRemoveTitle: 'Confirmation de la suppression',
          confirmRemoveText: 'Êtes-vous sûr de supprimer la signature ?',
          confirmRemoveOk: 'Oui',
          removeSignatureTooltip: 'Retirer la signature',
        },
        acknowledgment: 'A/R recommandé',
        acknowledgmentJson: 'A/R recommande (JSON)',
        attendanceSheet: {
          button: 'Feuille de présence',
          save: 'Enregistrer',
          saveSuccess: 'Feuille de présence sauvegardée',
          reset: 'Réinitialiser',
          resetSuccess: 'Feuille de présence réinitialisée',
          requiredValidation:
            'La feuille de présence doit être validée avant de pouvoir exécuter cette action.',
          resetTooltip: `Revenir à la feuille de présence d'origine`,
          doNotDelete: 'Ne pas supprimer ce bloc',
          resetConfirm:
            'Voulez-vous vraiment réinitialiser la feuille de présence ?{br}Cette action est irréversible.',
          ok: 'Oui',
          nok: 'Non',
          print: 'Imprimer',
          cancel: 'Annuler',
          validate: 'Valider',
          validateSuccess: 'Feuille de présence validée',
          pdfTitle: 'FDP-{date}',
          title: 'Feuille de présence',
          subtitle: 'Assemblée générale du {date}',
          coOwnershipName: 'Copropriété',
          key: 'Clé',
          coOwners: 'Copropriétaires',
          lots: 'Lots',
          tantiemes: 'Tant.',
          coOwnerPresents: 'Présents',
          coOwnerRepresented: 'Représentés',
          representedBy: 'Représenté par',
          externalRepresentative: 'Mandataire extérieur',
          remotelyPresent: 'A participé par visioconférence',
          onlineVote: 'A voté par en ligne',
          postalVote:
            'A voté par correspondance,{br}accusé réception le {importDate}',
          total: 'Total général',
          coOwnersTotal: 'Nombre de copropriétaires {coOwnersTotal}',
          summary: `Certifie sincère et véritable la présente feuille de présence arrêtée à {votingCoOwnersCount} {votingCoOwnersCount, plural,
            =0 {copropriétaire présent, représenté ou votant par correspondance totalisant}
            one {copropriétaire présent, représenté ou votant par correspondance totalisant}
            other {copropriétaires présents, représentés ou votants par correspondance totalisants ensemble}} {votingCoOwnersTantiemes} tantièmes.`,
          president: 'LE PRÉSIDENT',
          scrutineers: `{scrutineersCount, plural,
                one {LE SCRUTATEUR}
                other {LES SCRUTATEURS}}`,
          secretary: 'LE SECRÉTAIRE',
          sign: 'Signer',
          signatureTitle: 'Signature de {coOwnerName}',
          clear: 'Effacer',
          signatureError: 'Signature invalide',
          signaturePrerequisites:
            'Veuillez signer dans la zone prévue avant de valider.',
          removeSignature: 'Retirer la signature',
          removeSignatureTooltip: 'Cliquez pour retirer votre signature',
          confirmRemoveTitle: 'Confirmation de la suppression',
          confirmRemoveText:
            'Êtes-vous sûr de vouloir retirer votre signature ?',
          confirmRemoveOk: 'Oui',
        },
        balancesheet: {
          print: 'Imprimer',
          cancel: 'Annuler',
          button: 'Bilan',
          title:
            "Bilan technique de l'assemblée générale{br}{coOwnershipName}{br}du {date}",
          registeringStep: {
            title: 'Généralités',
            assemblyStartDate: "Début de l'AG :",
            assemblyCloseDate: "Fin de l'AG :",
            assemblyDuration: "Durée totale de l'AG :",
            phisycalPersonsRegistered: 'Personnes physiques émargées :',
            totalPersonsRepresented: 'Personnes présentes et représentées',
          },
          votingStep: {
            title: 'Votes',
            firstOpeningDateVote: 'Ouverture du premier vote :',
            lastClosureDateVote: 'Fermeture du dernier vote :',
            assemblyDuration: "Durée de l'AG (hors émargement) :",
            voteDuration: "Durée d'un vote (min. / moy. / max.) :",
            debatesDuration: 'Durée cumulée des débats :',
          },
          missingCoOwnersList: {
            missingCoOwners: 'Copropriétaires absents et non représentés',
            beforeOpeningVote: "avant l'ouverture des votes",
            missingCoOwnersTantiemes:
              'Soit <b>{missingCoOwnersCount}</b> / <b>{totalCoOwners}</b> absents totalisant <b>{missingCoOwnersTantiemes}</b> / <b>{totalTantiemes}</b> tantièmes.',
          },
          resolutions: {
            title: 'Résolutions',
            columns: {
              resolutionNumber: 'num',
              resolutionTitle: 'rés.',
              majority: 'majo.',
              action: 'action',
              time: 'heure',
              duration: 'durée',
              inFavor: 'pour',
              against: 'contre',
              abstention: 'abst.',
              nonExpressed: 'deff.',
              present: 'présents',
              missing: 'absents',
              total: 'total',
            },
            majorityArticles: resolutionMajorityArticlesShortcuts,
            openingVoteAction: 'Ouverture',
            closureVoteAction: 'Fermeture',
            informationPoint: "Point d'information",
            hour: 'Heure',
            debatesDuration: 'Durée des débats : {debatesDuration}',
            incomingCoOwner:
              '{incomingCoOwnerDate} - Arrivée {incomingCoOwnerName}',
            incomingTotal: 'Total arrivées',
            leavingCoOwner:
              '{leavingCoOwnerDate} - Départ {leavingCoOwnerName}',
            leavingTotal: 'Total départs',
            leaving: 'Départ',
            incoming: 'Arrivée',
            errors: {
              countError: `{countErrors, plural,
            one {<b>1 anomalie</b> a été détectée}
            other {<b>{countErrors} anomalies</b> ont été détectées}} sur cette résolution.`,
              resolveErrors: `Résoudre {countErrors, plural,
            one {l'anomalie}
            other {{countErrors} les anomalies}}`,
              missing_vote: `Le vote du copropriétaire {coOwner.name} n'a pas été pris en compte.`,
              invalid_votes_result: `Le résultat issu de la clôture de la résolution diffère du résultat des votes en base.`,
              invalid_voting_co_owners_tantiemes: `Le total des tantièmes calculé avant le vote diffère de celui calculé à partir des votes en base.`,
              invalid_votes_count_by_choice: `Le total du nombre de votants "{voteChoice}" diffère de celui obtenu à partir des votes en base.`,
              invalid_votes_tantiemes_by_choice: `Le total du nombre de tantièmes "{voteChoice}" diffère de celui obtenu à partir des votes en base.`,
              voteChoices: voteChoices,
            },
          },
        },
        acknowledgmentFile: {
          code: 'Code',
          civility: 'Civilité',
          name: 'Nom',
          address: 'Adresse',
          postalCode: 'Code Postal',
          city: 'Ville',
          shipmentType: "Type d'envoi",
        },
        summary: 'Bilan',
      },
      details: {
        title: 'Détails',
        coOwnersWarning:
          'Les copropriétaires ne sont pas complets, veuillez les réimporter.',
        resolutionsCard: {
          title: 'Résolutions',
          seeButton: 'voir',
          ok: 'Ok',
          cancel: 'Annuler',
          deleteResolutions: 'Supprimer toutes les résolutions',
          confirmResolutionsDeletion:
            'Voulez-vous vraiment supprimer toutes les résolutions ? Tous les votes associés,{br}y compris par correspondance seront supprimés également.',
        },
        coOwnersCard: {
          title: 'Copropriétaires',
          seeButton: 'voir',
          ok: 'Ok',
          cancel: 'Annuler',
          deleteCoOwners: 'Supprimer tous les copropriétaires',
          debugCoOwners: 'Corriger les erreurs',
          confirmCoOwnersDeletion:
            'Voulez-vous vraiment supprimer tous les copropriétaires ? Les clés de répartitions seront également supprimées.',
          debuggingModal: {
            title: 'Anomalies détectées',
            description: `Il semblerait que les méthodes de vote publiques des copropriétaires suivants ne correspondent pas avec leur réelle méthode de vote (ils n'apparaissent sans doute pas comme présents alors qu'ils le sont) :`,
            confirmCoOwnersDebugging:
              'Voulez-vous rétablir les bonnes méthodes de vote ?',
            buttonOk: 'Corriger',
          },
        },
        allocationKeysCard: {
          title: 'Clés de répartition',
          list: 'Liste',
          tabContent: {
            coOwnerColumn: 'Copropriétaire',
            tantiemesColumn: 'Tantièmes',
            lotsColumn: 'Lots',
            total: 'Total',
          },
          tabList: {
            allocationKeyColumn: 'Nom',
            tantiemesColumn: 'Tantièmes',
            coOwnersCountColumn: 'Nombre de copropriétaires',
            lotsCountColumn: 'Nombre de lots',
          },
        },
        votesCard: {
          title: 'Votes',
          seeButton: 'voir',
          ok: 'Ok',
          doNotDeletePostalVotes: 'Supprimer hormis VPC',
          cancel: 'Annuler',
          deleteVotes: 'Supprimer tous les votes',
          confirmVotesDeletion:
            'Voulez-vous vraiment supprimer tous les votes ?',
        },
        votesTable: {
          title: 'Votes',
          columnCoOwnerName: 'Copropriétaire',
          columnCoOwnerId: 'ID',
          voteChoices: voteChoices,
          totalRecords: `{totalRecords, plural,
            =0 {0 copropriétaire trouvé}
            one {1 copropriétaire trouvé}
            other {{totalRecords} copropriétaires trouvés}}`,
          nonAuthorizedActionTitle: 'Action non permise',
          nonAuthorizedActionContent:
            'Vote clos ou copropriétaire non votant par correspondance.',
          nonAuthorizedActionSearch:
            'Vous devez entrer un minimum de 2 caractères pour une recherche valide.',
        },
        searchCoOwner: {
          searchInformation:
            "Seul le nom et l'id sont disponibles à la recherche. (2 caractères minimums sont requis)",
          searchInput: 'Rehercher un copropriétaire...',
        },
      },
      registration: {
        back: 'Retour',
        loginCodeInput: {
          placeholder: 'Numéro de badge',
          validate: 'Valider',
        },
        coOwnerSelection: {
          externalRepresentative: 'Mandataire extérieur',
          selectCoOwner: 'Selectionnez un copropriétaire',
          next: 'Suivant',
        },
        externalRepresentative: {
          externalRepresentativeName: 'Nom du mandataire extérieur',
          next: 'Suivant',
        },
        representationQuestion: {
          title: 'Avez-vous des pouvoirs ?',
          yes: 'OUI',
          no: 'NON',
        },
        representationSelection: {
          selectRepresentedCoOwners:
            'Selectionnez les copropriétaires représentés',
          isOneResolutionVoteOpen:
            "Un vote est en cours, merci de patienter jusqu'à sa fermeture",
          next: 'Suivant',
        },
        summary: {
          modifyRepresentation: 'Modifier les pouvoirs',
          loginCode: 'Badge #{loginCode}',
          tantiemes: `{tantiemes, plural,
            =0 {Aucun tantième}
            one {1 tantième}
            other {{tantiemes} tantièmes}}`,
          personnalTantiemes: 'Tantiemes personnels',
          total: 'Total',
          next: 'Suivant',
          exit: 'Sortir',
          exitQuestion:
            "Voulez-vous vraiment faire sortir {coOwnerName} de l'assemblée ?",
          confirm: 'Confirmer',
          cancel: 'Annuler',
          isOneResolutionVoteOpen:
            "Un vote est en cours, merci de patienter jusqu'à sa fermeture",
          leave: 'Quitter',
        },
        signature: {
          title: 'Signature',
          clear: 'Recommencer',
          validate: 'Valider',
          isOneResolutionVoteOpen:
            "Un vote est en cours, merci de patienter jusqu'à sa fermeture",
          signatureError: 'Signature non valide',
          signaturePrerequisites: 'Merci de faire une signature plus grande.',
        },
      },
      coOwnersSelect: {
        voteMethods: voteMethods,
        tantiemes: `{tantiemes, plural,
            =0 {Aucun tantième}
            one {1 tantième}
            other {{tantiemes} tantièmes}}`,
      },
    },
    login: {
      connection: 'Connexion',
      email: 'Email',
      password: 'Mot de passe',
      errors: {
        // see https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signinwithemailandpassword for an exhaustive list of error codes
        'auth/invalid-email':
          'Email invalide. Pour créer un compte, veuillez transmettre vos informations de contact à votre syndic.',
        'auth/user-disabled':
          "Votre compte a été désactivé. Pour plus d'informations, veuillez contacter votre syndic",
        'auth/user-not-found':
          'Adresse non reconnue. Pour créer un compte, veuillez transmettre vos informations de contact à votre syndic.',
        'auth/wrong-password': 'Mot de passe invalide.',
        'auth/unknown-error':
          "Une erreur s'est produite. Veuillez ré-essayer ultérieurement.",
      },
      passwordResetModal: {
        forgottenPassword: 'Créer / Réinitialiser son mot de passe',
        title: 'Créer / Réinitialiser son mot de passe',
        emailLabel: 'Entrez votre adresse email pour recevoir un courriel :',
        emailPlaceholder: 'Email',
        cancel: 'Annuler',
        reset: 'Créer / Réinitialiser',
        success:
          'Email envoyé. Pensez à vérifier votre boîte de réception ainsi que vos spams.',
        errors: {
          // see https://firebase.google.com/docs/reference/js/firebase.auth.Auth#sendpasswordresetemail for an exhaustive list of error codes
          'auth/invalid-email':
            'Email invalide. Pour créer un compte, veuillez transmettre vos informations de contact à votre syndic.',
          'auth/user-not-found':
            'Adresse non reconnue. Pour créer un compte, veuillez transmettre vos informations de contact à votre syndic.',
          'auth/unknown-error':
            "Une erreur s'est produite. Veuillez ré-essayer ultérieurement.",
        },
      },
      mfaVerificationModal: {
        title: 'Authentification 2 facteurs',
        mfaVerificationCodeLabel: 'Entrez le code de vérification reçu par SMS',
        mfaVerificationCodePlaceholder: '123456',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        errors: {
          'auth/invalid-verification-code': 'Code invalide',
        },
      },
    },
    coOwner: {
      generalAssembly: {
        voteChoice: {
          choiceTitle: 'Je choisis de voter',
          represented: 'Je vote par procuration',
          representedCard: 'Par procuration',
          representedHelp:
            "Je donne mon pouvoir de vote à un autre copropriétaire de mon choix, qui votera pour moi à l'assemblée générale.",
          physicallyPresent: 'Je me rendrai sur place.',
          physicallyPresentCard: 'Sur place',
          physicallyPresentHelp:
            "Je viendrai en présentiel le jour de l'assemblée générale.",
          remotelyPresent: "J'y assisterai en visioconférence",
          remotelyPresentCard: 'En visioconférence',
          remotelyPresentHelp:
            "Je me connecterai depuis mon ordinateur à l'assemblée générale.",
          earlyOnlineVote: "Je vote en ligne à l'avance",
          earlyOnlineVoteCard: "En ligne à l'avance",
          earlyOnlineVoteHelp:
            "Je décide de voter en ligne à l'avance, sans avoir besoin d'envoyer mon bulletin de vote par correspondance au syndic par voie postale.",
          earlyPostalVote: 'Je vote par correspondance.',
          earlyPostalVoteCard: 'Par correspondance',
          earlyPostalVoteHelp:
            "Je décide d'informer le Syndic que j'enverrai mon bulletin de vote par voie postale.",
        },
        earlyOnlineVoteForm: {
          tantiemesReminder:
            "Pour rappel, vos tantièmes s'élèvent à { coOwnerTantiemes }, soit { tantiemesFrac, number, ::scale/100 .00 }% des tantièmes totaux ({ generalAssemblyTantiemes }).",
          timeWarning:
            "Vous pouvez modifier vos votes jusqu'à 24h avant l'heure de convocation.",
          modifyVoteMethod: 'Modifier mon moyen de vote',
          resolutions: 'Résolutions',
          voteDisabled: 'Sans vote',
          missingAllocationKey:
            "Vous n'êtes pas éligible au vote car vous ne possédez pas de lots concernant cette clé de répartition.",
          submitVotes: 'Je valide mes votes',
          resolutionMajorityArticlesLinks: resolutionMajorityArticlesLinks,
          inLineMajorityArticleConsultation:
            'Consultez la documention en ligne sur <a>legifrance</a> concernant cette article.',
        },
        earlyOnlineVoteSuccess: {
          title:
            "Vous avez voté en ligne à l'avance. Vos votes ont bien été pris en compte.",
          subTitle:
            "Vous avez jusqu'à 24h avant l'assemblée générale pour les modifier.",
          modifyVoteMethod: 'Je modifie mon moyen de vote',
          modifyVotes: 'Je modifie mes votes',
        },
        physicallyPresentSuccess: {
          title:
            "Vous avez choisi d'assister à l'assemblée générale sur place.",
          subTitle:
            "Merci de vous rendre à l'adresse suivante: { address } le { dateDay } à { dateTime }.",
          modifyVoteMethod: 'Je modifie mon moyen de vote',
          access: 'Accéder',
        },
        physicallyPresentVote: {
          noVote: "Il n'y a pas de vote en cours",
          voted:
            'Votre choix a bien été pris en compte pour la résolution {resolutionNumber}',
          tantiemes: `{tantiemes, plural,
            =0 {Aucun tantième}
            one {1 tantième}
            other {{tantiemes} tantièmes}}`,
          loginCode: 'Badge #{loginCode}',
          resolution: 'Résolution {resolutionNumber} :',
          validate: 'Valider',
          representationsCount: `Vous détenez {representationsCount, plural,
            one {1 pouvoir}
            other {{representationsCount} pouvoirs}} cumulant {tantiemesCount, plural,
            one {tantième}
            other {{tantiemesCount} tantièmes}}.`,
        },
        remotelyPresentSuccess: {
          title:
            "Vous avez choisi d'assister à l'assemblée générale en visioconférence.",
          subTitleBeforeAssemblyStarted:
            'Merci de vous connecter sur ce même lien le { dateDay } à { dateTime }.',
          subTitleBeforeCoOwnerSigned: `Merci de signer la feuille de présence dans l'encadré ci-dessous avant de rejoindre l'assemblée.`,
          subTitleAfterCoOwnerSigned: `Vous venez ou avez déjà signé la feuille de présence, vous pouvez désormais rejoindre l'assemblée générale.`,
          modifyVoteMethod: 'Je modifie mon moyen de vote',
          joinGeneralAssembly: "Rejoindre l'assemblée générale",
          isGeneralAssemblyNotOpen:
            "L'assemblée générale n'est pas encore ouverte",
          clear: 'Recommencer',
          validate: 'Valider',
          signatureSuccess: 'Votre signature a bien été prise en compte',
          signatureMissing: 'Signature manquante',
          signatureError: 'Signature non valide',
          signaturePrerequisites: 'Merci de faire une signature plus grande.',
        },
        remotelyPresentDashboard: {
          tantiemes: 'Tantièmes',
          vote: {
            coOwnerTantiemes: `Vous détenez <b>{tantiemesCount, plural,
            one {tantième}
            other {{tantiemesCount} tantièmes}}</b> sur la clé <b>{allocationKey}</b>`,
            noVotedResolution: "Il n'y a pas de vote en cours.",
            resolutionVote: 'Vote pour la résolution {resolutionNumber}',
            representationsCount: `Vous détenez {representationsCount, plural,
            one {1 pouvoir}
            other {{representationsCount} pouvoirs}} cumulant {tantiemesCount, plural,
            one {tantième}
            other {{tantiemesCount} tantièmes}}.`,
            openVoteGrid: 'Ouvrir la grille de vote',
            voteGrid: {
              selectForAll: 'Sélectionner pour tous',
              validate: 'Valider',
              title: 'Votes pour la résolution {resolutionNumber}',
              voteSaved: 'Vos votes ont bien été pris en compte.',
            },
            voteSaved: 'Votre vote a bien été pris en compte.',
            validate: 'Valider',
          },
          speech: {
            askSpeech: 'Demander la parole',
            cancelSpeechRequest: 'Annuler ma demande',
            muted: "Vous n'avez pas la parole.",
            isAskingSpeech: 'Vous avez demandé la parole.',
            isSpeaking: 'Vous avez la parole.',
            activateMicrophone: 'Activer mon micro',
            deactivateMicrophone: 'Désactiver mon micro',
          },
          presentCoOwners: {
            animator: 'Animateur',
            president: 'Président',
            hasSpeech: 'A la parole ',
            waitingLine:
              "Liste d'attente prise de parole ({waitingCoOwnersCount})",
            presentCoOwners: 'Participants ({presentCoOwnersCount})',
          },
          offline: {
            message: 'Problème réseau',
            description: 'Vous êtes actuellement hors ligne!',
          },
        },
        earlyPostalVoteSuccess: {
          title: 'Vous avez choisi de voter par correspondance.',
          subTitle:
            "Merci d'envoyer vos votes par courrier postal à votre syndic.",
          modifyVoteMethod: 'Je modifie mon moyen de vote',
        },
        represented: {
          coOwnerSelection: {
            modifyVoteMethod: 'Je modifie mon moyen de vote',
            representationQuestion:
              'À qui souhaitez-vous transmettre vos pouvoirs de vote ?',
            validate: 'Valider',
            coOwnersSelect: {
              voteMethods: voteMethods,
              tantiemes: `{tantiemes, plural,
                =0 {Aucun tantième}
                one {1 tantième}
                other {{tantiemes} tantièmes}}`,
            },
          },
          success: {
            representativeChoice:
              'Vous avez choisi de transmettre vos pouvoirs à :',
            changeRepresentative: 'Je souhaite changer de mandataire',
            modifyVoteMethod: 'Je modifie mon moyen de vote',
          },
        },
        closed: {
          title:
            "Récapitulatif de l'assemblée générale{br}{coOwnershipName}{br}du {date}",
          warning: "L'assemblée générale est terminée.",
          downloadSummary: 'Télécharger le récapitulatif',
          downloadButton: 'Télécharger',
          summary: 'Récapitulatif',
          pdfTitle: 'AG-{date}',
          signature: {
            title: 'Signer le procès Verbal',
            clear: 'Recommencer',
            save: 'Enregistrer',
            cancel: 'Annuler',
            signButton: 'Signer les documents',
            editingDocuments: "Les documents sont en cours d'édition",
            saveSuccess: 'Votre signature a bien été prise en compte',
            signatureError: 'Signature non valide',
            signaturePrerequisites: 'Merci de faire une signature plus grande.',
          },
          transcript: {
            title: 'Procès verbal',
            seeTranscript: 'Voir le procès verbal',
            cancel: 'Annuler',
            print: 'Imprimer',
            notPublish: "Le procès verbal est en cours d'édition.",
            signature: {
              president: 'LE PRÉSIDENT',
              scrutineers: `{scrutineersCount, plural,
                one {LE SCRUTATEUR}
                other {LES SCRUTATEURS}}`,
              secretary: 'LE SECRÉTAIRE',
            },
            pdfTitle: 'PV-{date}',
            editingDocument: "Le procès verbal est en cours d'édition",
            removeSignature: 'Retirer la signature',
            confirmRemoveTitle: 'Confirmation de la suppression',
            confirmRemoveText: 'Êtes-vous sûr de supprimer la signature ?',
            confirmRemoveOk: 'Oui',
            signButton: 'Signer',
          },
          attendanceSheet: {
            title: 'Feuille de présence',
            seeAttendanceSheet: 'Voir la feuille de présence',
            cancel: 'Annuler',
            print: 'Imprimer',
            notPublish: "La feuille de présence est en cours d'édition.",
            signature: {
              president: 'LE PRÉSIDENT',
              scrutineers: `{scrutineersCount, plural,
                one {LE SCRUTATEUR}
                other {LES SCRUTATEURS}}`,
              secretary: 'LE SECRÉTAIRE',
            },
            pdfTitle: 'FDP-{date}',
            editingDocument: "La feuille de présence est en cours d'édition",
            removeSignature: 'Retirer la signature',
            confirmRemoveTitle: 'Confirmation de la suppression',
            confirmRemoveText: 'Êtes-vous sûr de supprimer la signature ?',
            confirmRemoveOk: 'Oui',
            signButton: 'Signer',
          },
          resolutions: {
            columns: {
              number: 'Numéro',
              resolution: 'Résolutions',
              key: 'Clé',
              majority: 'Majorité',
              vote: 'Vote',
              time: 'Heure',
              status: 'Status',
            },
            majorityArticles: resolutionMajorityArticles,
            majorityArticlesLong: resolutionMajorityArticles,
            hour: 'Heure',
            voteChoices: voteChoices,
            voteResult: {
              [ResolutionVoteResult.ACCEPTED]: 'Adoptée',
              [ResolutionVoteResult.REFUSED]: 'Rejetée',
              NON_VOTED: 'Non votée',
              COMMENT: 'Commentaire',
            },
            date: 'Date',
            result: 'Résultat',
          },
          identityCards: {
            coOwnerId: 'Copropriétaire ID - ',
            identity: 'Identité',
            status: 'Statut',
            voteMethods: voteMethods,
            events: {
              title: 'Historique',
              coOwnerEventTypes: {
                [CoOwnerEventType.PHYSICALLY_JOINED]: 'Arrivée,',
                [CoOwnerEventType.PHYSICALLY_LEFT]: 'Sortie,',
                [CoOwnerEventType.REMOTELY_JOINED]: 'Connexion,',
                [CoOwnerEventType.REMOTELY_LEFT]: 'Déconnexion,',
                [CoOwnerEventType.ANNOUNCED_REPRESENTED_BY]:
                  'Don de pouvoir donné à {representingCoOwnerName},',
                [CoOwnerEventType.REPRESENTATIVE_ADDED]:
                  'Don de pouvoir donné à {representingCoOwnerName},',
                [CoOwnerEventType.REPRESENTATIVE_REMOVED]:
                  'Don de pouvoir retiré de {representingCoOwnerName},',
              },
              formatDate: ' le {date} à {time}',
            },
            address: 'Adresse',
            keys: `{keysCount, plural,
            =0 {Clé}
            one {Clé}
            other {Clés}} de répartition`,
            lots: 'Lot(s) : ',
            lot: 'Lot',
            tantiemes: 'Tantième(s) : ',
          },
        },
      },
    },
    syndic: {
      projectionMode: {
        resolutions: {
          openVote: 'Ouvrir le vote',
          projectResults: 'Afficher les résultats du vote',
          projectResolution: 'Projeter le texte de résolution',
          projectCommentary: 'Projeter',
          validateCommentary: 'Valider',
        },
        controlsAndCamera: {
          projectScreen: "Partager l'écran",
          unprojectScreen: "Arrêter le partage d'écran",
          projectRemainingVoters: 'Afficher votants restants',
          activateMic: 'Activer micro',
          deactivateMic: 'Désactiver micro',
          activateCam: 'Activer caméra',
          deactivateCam: 'Désactiver caméra',
          closeVote: 'Clôturer vote',
        },
        presentCoOwners: {
          president: 'Président',
          hasSpeech: 'A la parole',
          waitingLine:
            "Liste d'attente prise de parole ({waitingCoOwnersCount})",
          presentCoOwners: 'Participants ({presentCoOwnersCount})',
          representationSelection: {
            editRepresentations: 'Modifier les pouvoirs',
            cannotEditRepresentations:
              "Vous ne pouvez pas modifier les pouvoirs d'un copropriétaire pendant un vote",
            selectRepresentedCoOwners:
              'Selectionnez les copropriétaires représentés',
            validate: 'Valider',
          },
          endSpeech: "Mettre fin à l'intervention",
          cancelSpeechRequest: 'Annuler la demande',
          grantSpeech: 'Donner la parole',
        },
        tchat: {
          you: 'Vous',
        },
      },
      coOwnersSelect: {
        voteMethods: voteMethods,
        tantiemes: `{tantiemes, plural,
            =0 {Aucun tantième}
            one {1 tantième}
            other {{tantiemes} tantièmes}}`,
      },
    },
    components: {
      remoteDashboard: {
        projectedScreen: {
          welcome:
            "Bienvenue à tous, l'assemblée générale va bientôt commencer",
          tantiemes: `{representedCoOwnersCount, plural,
          =0 {Aucun copropriétaire présent ou représenté}
          one {1 copropriétaire présent ou représenté}
          other {{representedCoOwnersCount} copropriétaires présents ou représentés}
        } totalisant {representedTantiemes} {representedTantiemes, plural,
          =0 {tantième}
          one {tantième}
          other {tantièmes}} sur {tantiemesTotal} ({tantiemesFrac, number, ::scale/100 .00}%)`,
          projectedResolutionHeader: {
            resolutionNumber: 'Resolution {resolutionNumber}',
          },
          remainingCoOwnerVotes: {
            label: '{votesFrac, number, ::scale/100 .00}%',
            title: 'Votants restants',
            name: '{name}',
            representedCoOwnersCount: `({representedCoOwnersCount, plural,
            =0 {Aucun pouvoir}
            one {1 pouvoir}
            other {{representedCoOwnersCount} pouvoirs}})`,
            [VoteMethod.PHYSICALLY_PRESENT]: 'sur place',
            [VoteMethod.REMOTELY_PRESENT]: 'en visio',
            remainingVotes: 'Votants restants',
            doneVotes: 'Ont voté',
          },
          voteResults: {
            label:
              '{tantiemes} tant. ({tantiemesFrac, number, ::scale/100 .00}%)',
            [ResolutionVoteResult.ACCEPTED]: 'Adoptée',
            [ResolutionVoteResult.REFUSED]: 'Rejetée',
            [VotePresency.VOTING]: 'Présents',
            [VotePresency.NOT_VOTING]: 'Absents',
            [VoteChoice.IN_FAVOR]: 'Pour',
            [VoteChoice.AGAINST]: 'Contre',
            [VoteChoice.ABSTENTION]: 'Abstentions',
            [VoteChoice.NOT_EXPRESSED]: 'Défaillants',
          },
          secondReadingModal: {
            ok: 'Ok',
            cancel: 'Annuler',
            doNotCopyPostalVotes: 'Ne pas copier les VPC',
            button: ' Seconde lecture disponible',
            titlePart1: 'Créer une résolution pour une seconde lecture 1/2',
            resolutionMajoritySecondReading: resolutionMajoritySecondReading,
          },
          copyVotesModal: {
            ok: 'Ok',
            cancel: 'Annuler',
            titlePart2: 'Créer une résolution pour une seconde lecture 2/2',
            doNotCopyPostalVotes: 'Ne pas copier les VPC',
            button: 'Créer résolution seconde lecture',
            manuallyOpenVote: 'Non, ouvrir vote manuellement',
            content:
              'Voulez-vous copier les résultats de votes de la précèdente résolution (autres que les votes par correspondance) et clotûrer le vote ?',
          },
        },
      },
    },
    registratedLogin: {
      generalAssemblyLoginCode: {
        label: 'Code Assemblée Générale',
        placeholder: '123456',
      },
      coOwnerLoginCode: {
        label: 'Code copropriétaire',
        placeholder: '123A',
      },
      connection: 'Connexion',
    },
  },
  antMedia: {
    errors: {
      NotConnected: 'Erreur ! Le serveur ne répond pas.',
      NotAllowedError:
        "Erreur ! Merci de donner la permission d'utiliser le micro ou la caméra.",
      NotReadableError:
        "Erreur ! La caméra ou le micro ne semblent pas disponibles. Merci de vérifier qu'ils ne sont pas utilisés par d'autres applications.",
      NotFoundError:
        "Erreur ! La caméra ou le micro n'ont pas été détectés correctement. Merci de vérifier qu'ils sont fonctionnels et autorisés à s'exécuter.",
      UnauthorizedAccess:
        "Erreur ! Vous n'avez pas la permission d'accéder à cette conférence",
      ScreenSharePermissionDenied:
        "Erreur ! Le partage d'écran a été refusé. Merci d'autoriser le partage de l'écran pour utiliser cette fonctionnalité.",
    },
  },
};

export type Translations = typeof fr;
